import { TypographyVariantsOptions, createTheme } from '@mui/material/styles';

import zeplinColor from './Color';

interface ExtendedTypographyOptions extends TypographyVariantsOptions {
  buttonLarge: React.CSSProperties;
  buttonMedium: React.CSSProperties;
}

declare module '@mui/material/styles/createPalette' {
  interface PaletteOptions {
    customDisable?: {
      main: string;
    };
    customSecondary?: {
      main?: string;
    };
  }
}

declare module '@mui/material/Button' {
  interface ButtonPropsColorOverrides {
    customDisable: true;
    customSecondary: true;
  }
}

declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    body3: true;
    responsiveBody2: true;
    responsiveBody3: true;

    label1: true;
    label2: true;
    responsiveLabel1: true;
    responsiveLabel2: true;

    heading1: true;
    heading2: true;
    heading3: true;
    heading4: true;
    responsiveH1: true;
    responsiveHeading1: true;
    responsiveHeading2: true;
    responsiveHeading3: true;
    responsiveHeading4: true;
    responsiveHeading5: true;
    responsiveHeading6: true;

    display4: true;
    display5: true;
    display6: true;
    responsiveDisplay4: true;
    responsiveDisplay5: true;
    responsiveDisplay6: true;

    responsiveCaption: true;

    responsiveButtonLarge: true;
    responsiveButtonMedium: true;
    responsiveButtonSmall: true;

    buttonLarge: true;
    buttonMedium: true;
    buttonSmall: true;
  }
}
interface ExtendedTypographyOptions extends TypographyVariantsOptions {
  body3: React.CSSProperties;
  responsiveBody3: React.CSSProperties;
  responsiveBody2: React.CSSProperties;

  label1: React.CSSProperties;
  label2: React.CSSProperties;
  responsiveLabel1: React.CSSProperties;
  responsiveLabel2: React.CSSProperties;

  heading1: React.CSSProperties;
  heading2: React.CSSProperties;
  heading3: React.CSSProperties;
  heading4: React.CSSProperties;
  responsiveH1: React.CSSProperties;
  responsiveHeading1: React.CSSProperties;
  responsiveHeading2: React.CSSProperties;
  responsiveHeading3: React.CSSProperties;
  responsiveHeading4: React.CSSProperties;
  responsiveHeading5: React.CSSProperties;
  responsiveHeading6: React.CSSProperties;

  display4: React.CSSProperties;
  display5: React.CSSProperties;
  display6: React.CSSProperties;
  responsiveDisplay4: React.CSSProperties;
  responsiveDisplay5: React.CSSProperties;
  responsiveDisplay6: React.CSSProperties;

  responsiveCaption: React.CSSProperties;

  buttonSmall: React.CSSProperties;
  buttonMedium: React.CSSProperties;
  buttonLarge: React.CSSProperties;
  responsiveButtonSmall: React.CSSProperties;
  responsiveButtonMedium: React.CSSProperties;
  responsiveButtonLarge: React.CSSProperties;
}

const appTheme = createTheme({
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 900,
      lg: 1200,
      xl: 1700,
    },
  },
  typography: {
    fontFamily: 'HelveticaNeue',
    textTransform: 'none',
    h1: {
      fontSize: 16,
      fontWeight: 550,
      textTransform: 'none',
    },
    responsiveH1: {
      fontSize: 16,
      fontWeight: 550,
      textTransform: 'none',
    },
    h2: {
      fontSize: 16,
      fontWeight: 400,
      textTransform: 'none',
    },

    h3: {
      fontSize: 32,
      fontWeight: 600,
      textTransform: 'none',
    },
    h4: {
      fontSize: 28,
      fontWeight: 300,
      textTransform: 'none',
    },
    h5: {
      fontSize: 22,
      fontWeight: 550,
      textTransform: 'none',
    },
    h6: {
      fontSize: 20,
      fontWeight: 600,
      textTransform: 'none',
    },
    heading1: {
      fontSize: 22,
      fontWeight: 550,
    },
    responsiveHeading1: {
      fontSize: 22,
      fontWeight: 550,
    },
    heading2: {
      fontSize: 20,
      fontWeight: 550,
    },
    responsiveHeading2: {
      fontSize: 20,
      fontWeight: 550,
    },
    responsiveHeading3: {
      fontSize: 18,
      fontWeight: 'bold',
    },
    heading3: {
      fontSize: 18,
      fontWeight: 'bold',
    },
    heading4: {
      fontSize: 16,
      fontWeight: 550,
    },
    responsiveHeading4: {
      fontSize: 16,
      fontWeight: 550,
    },
    responsiveHeading5: {
      fontSize: 14,
      fontWeight: 500,
    },
    responsiveHeading6: {
      fontSize: 18,
      fontWeight: 700,
    },
    display4: {
      fontSize: 28,
      fontWeight: 700,
    },
    responsiveDisplay4: {
      fontSize: 28,
      fontWeight: 700,
    },
    display5: {
      fontSize: 24,
      fontWeight: 700,
      lineHeight: '29.3px',
    },
    responsiveDisplay5: {
      fontSize: 24,
      fontWeight: 700,
      lineHeight: '29.3px',
    },
    display6: {
      fontSize: 20,
      fontWeight: 700,
      lineHeight: '29.3px',
    },
    responsiveDisplay6: {
      fontSize: 20,
      fontWeight: 700,
      lineHeight: '29.3px',
    },
    body1: {
      fontSize: 16,
      fontWeight: 400,
      textTransform: 'none',
    },
    body2: {
      fontSize: 16,
      fontWeight: 400,
      lineHeight: '19.09px',
      letterSpacing: '0.005em',
      textTransform: 'none',
    },
    responsiveBody2: {
      fontSize: 16,
      fontWeight: 400,
      lineHeight: '19.09px',
      letterSpacing: '0.005em',
      textTransform: 'none',
    },
    body3: {
      fontSize: 14,
      fontWeight: 400,
      lineHeight: '16.7px',
      textTransform: 'none',
    },

    responsiveBody3: {
      fontWeight: 400,
      fontSize: 14,
      lineHeight: '16.7px',
      textTransform: 'none',
    },
    responsiveLabel1: {
      fontWeight: 400,
      fontSize: 12,
    },

    label1: {
      fontSize: 12,
      fontWeight: 400,
    },
    label2: {
      fontSize: 10,
      fontWeight: 400,
    },
    responsiveLabel2: {
      fontSize: 10,
      fontWeight: 400,
    },
    caption: {
      fontSize: 12,
      fontWeight: 400,
    },
    responsiveCaption: {
      fontSize: 12,
      fontWeight: 400,
    },
    buttonLarge: {
      fontSize: 15,
      fontWeight: 600,
      textTransform: 'none',
    },
    buttonMedium: {
      fontSize: 16,
      fontWeight: 500,
      textTransform: 'none',
    },
    responsiveButtonLarge: {
      fontSize: 15,
      fontWeight: 500,
      textTransform: 'none',
    },
    responsiveButtonMedium: {
      fontSize: 16,
      fontWeight: 500,
      textTransform: 'none',
    },

    buttonSmall: {
      fontSize: 14,
      fontWeight: 400,
      textTransform: 'none',
    },
    responsiveButtonSmall: {
      fontSize: 14,
      fontWeight: 400,
      textTransform: 'none',
    },
  } as ExtendedTypographyOptions,
  palette: {
    primary: {
      main: zeplinColor.Brand1,
    },
    secondary: {
      main: zeplinColor.Neutral9,
    },
    customDisable: {
      main: zeplinColor.Neutral5,
    },
    text: {
      primary: zeplinColor.BasicBlack,
      secondary: zeplinColor.Neutral8,
    },
    error: {
      main: zeplinColor.Error,
    },

    background: {
      default: zeplinColor.Neutral1,
      paper: zeplinColor.Neutral1,
    },

    customSecondary: {
      main: zeplinColor.Neutral1,
    },
  },
  components: {
    MuiTypography: {
      variants: [
        {
          props: { variant: 'responsiveButtonLarge' },
          style: ({ theme }) => ({
            fontSize: 12,
            [theme.breakpoints.up('xl')]: {
              fontSize: 15,
            },
          }),
        },
        {
          props: { variant: 'responsiveButtonMedium' },
          style: ({ theme }) => ({
            fontSize: 12,
            [theme.breakpoints.up('xl')]: {
              fontSize: 16,
            },
          }),
        },
        {
          props: { variant: 'responsiveButtonSmall' },
          style: ({ theme }) => ({
            fontSize: 12,
            [theme.breakpoints.up('xl')]: {
              fontSize: 14,
            },
          }),
        },
        {
          props: { variant: 'responsiveDisplay6' },
          style: ({ theme }) => ({
            fontSize: 16,
            [theme.breakpoints.up('xl')]: {
              fontSize: 20,
            },
          }),
        },
        {
          props: { variant: 'responsiveDisplay5' },
          style: ({ theme }) => ({
            fontSize: 18,
            [theme.breakpoints.up('xl')]: {
              fontSize: 24,
            },
          }),
        },
        {
          props: { variant: 'responsiveDisplay4' },
          style: ({ theme }) => ({
            fontSize: 24,
            [theme.breakpoints.up('xl')]: {
              fontSize: 28,
            },
          }),
        },
        {
          props: { variant: 'responsiveBody3' },
          style: ({ theme }) => ({
            fontSize: 14,
            [theme.breakpoints.up('xl')]: {
              fontSize: 14,
            },
          }),
        },
        {
          props: { variant: 'responsiveHeading1' },
          style: ({ theme }) => ({
            fontSize: 18,
            [theme.breakpoints.up('xl')]: {
              fontSize: 22,
            },
          }),
        },
        {
          props: { variant: 'responsiveHeading2' },
          style: ({ theme }) => ({
            fontSize: 16,
            [theme.breakpoints.up('xl')]: {
              fontSize: 20,
            },
          }),
        },
        {
          props: { variant: 'responsiveHeading3' },
          style: ({ theme }) => ({
            fontSize: 16,
            [theme.breakpoints.up('xl')]: {
              fontSize: 18,
            },
          }),
        },
        {
          props: { variant: 'responsiveHeading4' },
          style: ({ theme }) => ({
            fontSize: 14,
            [theme.breakpoints.up('xl')]: {
              fontSize: 16,
            },
          }),
        },
        {
          props: { variant: 'responsiveHeading5' },
          style: ({ theme }) => ({
            [theme.breakpoints.up('xl')]: {
              fontSize: 16,
            },
          }),
        },
        {
          props: { variant: 'responsiveHeading6' },
          style: ({ theme }) => ({
            [theme.breakpoints.up('xl')]: {
              fontSize: 20,
            },
          }),
        },
        {
          props: { variant: 'responsiveLabel1' },
          style: ({ theme }) => ({
            fontSize: 12,
            [theme.breakpoints.up('xl')]: {
              fontSize: 12,
            },
          }),
        },
        {
          props: { variant: 'responsiveLabel2' },
          style: ({ theme }) => ({
            fontSize: 10,
            [theme.breakpoints.up('xl')]: {
              fontSize: 10,
            },
          }),
        },
        {
          props: { variant: 'responsiveBody2' },
          style: ({ theme }) => ({
            fontSize: 14,
            [theme.breakpoints.up('xl')]: {
              fontSize: 16,
            },
          }),
        },
        {
          props: { variant: 'responsiveH1' },
          style: ({ theme }) => ({
            fontSize: 12,
            [theme.breakpoints.up('xl')]: {
              fontSize: 16,
            },
          }),
        },
        {
          props: { variant: 'responsiveCaption' },
          style: ({ theme }) => ({
            fontSize: 12,
            [theme.breakpoints.up('xl')]: {
              fontSize: 12,
            },
          }),
        },
      ],
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          borderRadius: 14,
          '& .MuiOutlinedInput-notchedOutline:hover': {
            borderColor: zeplinColor.Neutral9,
          },
          '& .Mui-focused.MuiOutlinedInput-notchedOutline': {
            borderColor: zeplinColor.Success,
          },
        },
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          color: zeplinColor.Neutral9,
          '&.Mui-error': {
            color: zeplinColor.Error,
          },
        },
      },
    },

    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: zeplinColor.Neutral10,
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: ({ theme }) => ({
          borderRadius: 14,
          [theme.breakpoints.down('xl')]: {
            borderRadius: 50,
          },
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: zeplinColor.Neutral9,
          },
          '&:hover:not(.Mui-focused):not(.Mui-error) .MuiOutlinedInput-notchedOutline': {
            borderColor: zeplinColor.Neutral9,
          },
          '&.Mui-focused': {
            borderColor: zeplinColor.Success,
          },
        }),
      },
    },
  },
});

export default appTheme;
